import { HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getInstrument, DEFAULT_INSTRUMENT_ID } from '@/lib/schema/loader';
import { AdministrationSections } from '@/components/rubric/AdministrationSections';
import { GlossaryDialog } from '@/components/rubric/GlossaryDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

/**
 * Help overlay reachable from the brand header's "?" button. Covers the
 * three things observers most often ask about:
 *
 *   1. What Y/N/NA mean and where NA applies.
 *   2. How the 1–7 score is computed (rule 1 short-circuit + walk-from-7).
 *   3. Keyboard shortcuts for the indicator radiogroup.
 *
 * Kept intentionally concise — linking to the printed FCCERS-3 manual for
 * the full rubric rationale. The manual is the source of truth; this tool
 * is a digital scoring aid.
 */
export function HelpDialog() {
  const administration =
    getInstrument(DEFAULT_INSTRUMENT_ID)?.administration ?? [];
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Help">
          <HelpCircle className="h-5 w-5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>How FCCERS scoring works in this tool</DialogTitle>
          <DialogDescription>
            Quick reference for Y / N / NA, the 1–7 score, and keyboard use.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5 text-sm leading-relaxed">
          <section>
            <h3 className="font-serif text-base font-semibold text-dece-navy">
              Indicator responses
            </h3>
            <ul className="mt-1 list-inside list-disc space-y-1 text-foreground/90">
              <li>
                <strong>Yes</strong> — the indicator is clearly met.
              </li>
              <li>
                <strong>No</strong> — the indicator is not met.
              </li>
              <li>
                <strong>NA</strong> — only available when the indicator is
                marked NA-eligible in the rubric. NA means the indicator
                genuinely does not apply to this program (e.g. no children
                in diapers during the observation).
              </li>
            </ul>
          </section>
          <section>
            <h3 className="font-serif text-base font-semibold text-dece-navy">
              How scores are calculated
            </h3>
            <p className="mt-1 text-foreground/90">
              The item score is the highest rule you meet, walking from 7
              down — with one exception: if{' '}
              <em>any</em> level-1 indicator is Yes, the item scores 1
              regardless of higher-level strengths. That's the rubric's
              "inadequate" floor for safety-critical conditions.
            </p>
            <p className="mt-2 text-foreground/90">
              NA responses are excluded from both the "all must be Yes" and
              the "at least half must be Yes" rules. The manual states this
              without qualification: <em>"Indicators that are scored NA are
              not counted when determining the score for an Item"</em>{' '}
              (Scoring System, point 8). Items marked NA are likewise left
              out of the subscale and total averages.
            </p>
          </section>
          <section>
            <h3 className="font-serif text-base font-semibold text-dece-navy">
              Item-level NA
            </h3>
            <p className="mt-1 text-foreground/90">
              A handful of items (e.g. <em>Diapering/toileting</em>) offer
              an item-level <strong>Not Applicable</strong> toggle at the
              top of the page. Use it when the program does not cover the
              item at all — the item is then excluded from the subscale and
              total averages.
            </p>
          </section>
          <section>
            <h3 className="font-serif text-base font-semibold text-dece-navy">
              Keyboard shortcuts
            </h3>
            <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-foreground/90">
              <dt className="font-mono text-xs">Tab</dt>
              <dd>Next indicator row (the active response holds focus).</dd>
              <dt className="font-mono text-xs">← → ↑ ↓</dt>
              <dd>Cycle between Yes / No / NA within a row.</dd>
              <dt className="font-mono text-xs">Space / Enter</dt>
              <dd>Toggle the focused response on/off.</dd>
              <dt className="font-mono text-xs">Home / End</dt>
              <dd>Jump to the first or last choice in the row.</dd>
            </dl>
          </section>
          <section>
            <h3 className="font-serif text-base font-semibold text-dece-navy">
              Data &amp; privacy
            </h3>
            <p className="mt-1 text-foreground/90">
              Drafts are stored in this browser via IndexedDB and are not
              uploaded anywhere until you click <em>Submit</em>
              . Exported JSON files include observer name, email, site
              address and any free-text notes — handle them like the paper
              equivalent.
            </p>
          </section>

          {administration.length > 0 && (
            <>
              <div className="flex items-center justify-between gap-3 border-t pt-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  From the FCCERS-3 manual
                </p>
                <GlossaryDialog />
              </div>
              <AdministrationSections sections={administration} />
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
